/**
 * HTML string → typed Doc. The previous editor parsed via DOMParser and kept
 * the parsed `HTMLElement` tree as state, which meant `getComputedStyle()`
 * lied (detached document) and every read had to know about both inline-style
 * and live computed styles. The new parser walks the same DOMParser output
 * once and extracts pure data.
 *
 * Anything we don't recognise is preserved opaquely — top-level <svg> blocks
 * become SvgLayers (innerMarkup retained verbatim), and any inline-style
 * properties the parser doesn't model are captured into `extraStyles` on the
 * layer (`captureExtraStyles` below). serialize.ts re-emits them and
 * layer-renderer.tsx applies them, so unsupported CSS round-trips end-to-end.
 *
 * Runs in both the editor (native browser DOMParser) and on the backend
 * (jsdom). `./dom-env` installs jsdom's window classes onto globalThis at
 * module load when running on Node so the code below can use unqualified
 * `DOMParser`/`instanceof HTMLElement`/`Node.TEXT_NODE` identically to the
 * browser path.
 */

import "./dom-env";

import { parseAnimationBlocks } from "./animation-blocks";
import { cssColorToHex } from "./css-color";
import { parseFontFamilyName } from "./font-family";
import { HT_ID_ATTR, HT_MOTION_ATTR, ensureLayerIds } from "./ids";
import { type AdRefImageHints, resolveAdRefs } from "./resolve-ad-refs";
import type {
  Border,
  BorderRadius,
  BorderSide,
  CornerRadius,
  Doc,
  Fill,
  GradientStop,
  ImageLayer,
  Layer,
  LayerMotion,
  ObjectFit,
  RectLayer,
  Shadow,
  SvgLayer,
  TextAlign,
  TextAnchorX,
  TextAnchorY,
  TextLayer,
  TextResizing,
  TextVerticalAlign,
  TextShadow,
} from "./model";
import { hasRadius, normalizeRadius } from "./model";

const DEFAULT_CANVAS_WIDTH = 800;
const DEFAULT_CANVAS_HEIGHT = 600;

const TEXT_HTML_TAGS = new Set([
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "span",
  "a",
  "label",
  "button",
  "li",
]);

const INLINE_HTML_TAGS = new Set([
  "br",
  "span",
  "a",
  "em",
  "strong",
  "i",
  "b",
  "u",
  "s",
  "mark",
  "code",
  "sub",
  "sup",
  "small",
  "wbr",
  "kbd",
  "samp",
  "var",
  "time",
  "dfn",
  "ins",
  "del",
  "abbr",
  "cite",
  "q",
]);

const HT_LOCKED_ATTR = "data-ht-locked";
const HT_LABEL_ATTR = "data-label";
const DATA_SLOT_ATTR = "data-slot";
const DATA_LAYER_TYPE_ATTR = "data-layer-type";

// Properties parse.ts reads explicitly into the layer model. Anything an
// element has set inline that *isn't* in this set falls through to
// `extraStyles` so the renderer reapplies it. Names are kebab-case to match
// `el.style.item(i)` output. Listed as longhands because that's what the
// browser exposes when shorthands are set inline.
const POSITION_BASE_PROPS = new Set<string>([
  "position",
  "left",
  "top",
  "width",
  "height",
  // Logical aliases of width/height; passed through, they would pin a hugged axis the model leaves open.
  "inline-size",
  "block-size",
  "transform",
  "opacity",
]);
const FILL_BORDER_SHADOW_PROPS = new Set<string>([
  "background",
  "background-color",
  "background-image",
  "border",
  "border-width",
  "border-style",
  "border-color",
  "border-top",
  "border-right",
  "border-bottom",
  "border-left",
  "border-top-width",
  "border-right-width",
  "border-bottom-width",
  "border-left-width",
  "border-top-style",
  "border-right-style",
  "border-bottom-style",
  "border-left-style",
  "border-top-color",
  "border-right-color",
  "border-bottom-color",
  "border-left-color",
  // Logical-property longhands the agent or simplifier may emit. Without
  // these in the consume set, they leak through to `extraStyles` and the
  // serialized output ends up with conflicting per-side declarations
  // (logical longhands from extraStyles, then physical from the model).
  "border-block",
  "border-block-start",
  "border-block-end",
  "border-block-width",
  "border-block-start-width",
  "border-block-end-width",
  "border-block-style",
  "border-block-start-style",
  "border-block-end-style",
  "border-block-color",
  "border-block-start-color",
  "border-block-end-color",
  "border-inline",
  "border-inline-start",
  "border-inline-end",
  "border-inline-width",
  "border-inline-start-width",
  "border-inline-end-width",
  "border-inline-style",
  "border-inline-start-style",
  "border-inline-end-style",
  "border-inline-color",
  "border-inline-start-color",
  "border-inline-end-color",
  "border-radius",
  "border-top-left-radius",
  "border-top-right-radius",
  "border-bottom-left-radius",
  "border-bottom-right-radius",
  "box-shadow",
]);
const TEXT_PROPS = new Set<string>([
  "font",
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "color",
  "text-align",
  "text-decoration",
  "text-decoration-line",
  "text-decoration-color",
  "text-decoration-style",
  "line-height",
  "letter-spacing",
  "text-shadow",
]);
// Text never carries an overflow mask in the model: the clamp kit is modeled
// as `TextLayer.maxLines` (serialize re-emits the canonical declarations),
// and the rest of the clip set is dropped outright — a text box is a layout
// rect, never a mask, so no renderer can slice glyphs. Legacy masked markup
// heals on its first parse→save round trip.
const TEXT_CLAMP_PROPS = new Set<string>([
  "-webkit-line-clamp",
  "-webkit-box-orient",
  "overflow",
  "overflow-x",
  "overflow-y",
  "overflow-block",
  "overflow-inline",
  "text-overflow",
]);
const IMAGE_PROPS = new Set<string>(["object-fit"]);

// Modern CSS properties the simplifier emits but the backend CSSOM (`cssstyle`)
// does not recognize and silently drops, so they never appear in the parsed
// CSSStyleDeclaration. `captureExtraStyles` recovers these from the raw style
// attribute so the backend parse matches the browser (the in-editor parse). The
// `text-wrap-*` / `white-space-collapse` longhands are what `white-space:
// nowrap` headlines serialize to in modern Chrome — dropping them lets a
// pinned single-line headline reflow on export. Keep in sync with the
// simplifier's text-rendering overrides.
const CSSOM_DROPPED_RECOVERABLE = new Set<string>([
  "object-view-box",
  "text-wrap-mode",
  "text-wrap",
  "text-wrap-style",
  "white-space-collapse",
  "-webkit-text-stroke",
  "-webkit-text-stroke-width",
  "-webkit-text-stroke-color",
  // Load-bearing with -webkit-text-stroke: without `paint-order: stroke` the
  // centered stroke paints over the fill, so dropping it on an editor save
  // visibly degrades outlined ("sticker") text.
  "paint-order",
]);

type LayerKind = "rect" | "text" | "image" | "svg" | "group";

const parser = new DOMParser();

export interface ParseDocOptions {
  /**
   * Filename → S3 storage key map (from `ad_brief_asset(_versions).component_assets`).
   * When provided alongside `apiBase`, `ad:filename` references in `<img src>`,
   * SVG `<image>`/`<use>` hrefs, and inline-style `url(...)` values are
   * rewritten to `${apiBase}/agents/files?key=<S3 key>` URLs before layer
   * extraction, so the resulting Doc's `ImageLayer.src` is directly fetchable.
   */
  componentAssets?: Record<string, string> | null;
  /**
   * Base URL the resolved file URLs are prefixed with, including the `/api`
   * segment (e.g. `https://api.us-east-1.production.hightouch.com/api`).
   * `resolveAdRefs` appends `/agents/files?key=...`.
   */
  apiBase?: string;
  imageHints?: AdRefImageHints;
}

export function parseDoc(html: string, options?: ParseDocOptions): Doc {
  const dom = parser.parseFromString(
    `<!DOCTYPE html><body>${html}</body>`,
    "text/html",
  );
  // Strip stylesheets/scripts — they don't belong in the editor surface.
  for (const tag of Array.from(dom.querySelectorAll("style, script"))) {
    tag.remove();
  }

  const root = pickRoot(dom);
  if (options?.componentAssets && options?.apiBase) {
    // Rewrite `ad:` refs into regional `${apiBase}/agents/files?key=...` URLs
    // before layer extraction so ImageLayer.src lands fully resolved.
    resolveAdRefs(
      root,
      options.componentAssets,
      options.apiBase,
      options.imageHints,
    );
  }
  const width = readPxStyle(root, "width") ?? DEFAULT_CANVAS_WIDTH;
  const height = readPxStyle(root, "height") ?? DEFAULT_CANVAS_HEIGHT;
  // A CSS-wide keyword on the longhand must fall through to the shorthand.
  const background =
    readEffectiveStyle(root, "background-color") ??
    readEffectiveStyle(root, "background") ??
    null;

  // Ids that came from the markup itself (`data-ht-id`). A group carrying one
  // is addressable from outside the Doc — video overlay animation bindings
  // key off these — so trivial-group flattening must not erase it.
  const explicitIds = new Set<string>();
  for (const el of Array.from(root.querySelectorAll(`[${HT_ID_ATTR}]`))) {
    const id = el.getAttribute(HT_ID_ATTR);
    if (id) explicitIds.add(id);
  }

  const layers = ensureLayerIds(
    flattenTrivialGroups(
      Array.from(root.children)
        .map((child) => parseLayer(child))
        .filter((layer): layer is Layer => layer !== null),
      explicitIds,
    ),
    explicitIds,
  );

  return {
    width,
    height,
    background: background ? (cssColorToHex(background) ?? background) : null,
    layers,
  };
}

/**
 * Heal layer trees that have zero-sized layout-only Group wrappers — older
 * dom-to-html output emitted a bare `<div>` for any source element with
 * multiple positioned children, even when the wrapper had no own paint or
 * text. Those parse as Group layers at (0,0,0,0) with no styles, which makes
 * the canvas selectable nothing, the layer list noisy, and gives drag/resize
 * handles nothing to attach to. They're safe to flatten because their
 * children are already in canvas-relative absolute coordinates — the wrapper
 * adds no rendering or editing semantics.
 *
 * Newer dom-to-html output skips these wrappers at the source. This pass
 * exists to heal saved ads that predate that fix.
 */
function flattenTrivialGroups(
  layers: Layer[],
  explicitIds: ReadonlySet<string>,
): Layer[] {
  const out: Layer[] = [];
  for (const layer of layers) {
    if (layer.type === "group") {
      const children = flattenTrivialGroups(layer.children, explicitIds);
      if (isTrivialGroup(layer, explicitIds)) {
        out.push(...children);
      } else {
        out.push({ ...layer, children });
      }
    } else {
      out.push(layer);
    }
  }
  return out;
}

function isTrivialGroup(
  group: Layer & { type: "group" },
  explicitIds: ReadonlySet<string>,
): boolean {
  return (
    group.x === 0 &&
    group.y === 0 &&
    group.width === 0 &&
    group.height === 0 &&
    group.rotation === 0 &&
    !group.flipX &&
    !group.flipY &&
    group.opacity === 1 &&
    !group.hidden &&
    !group.locked &&
    !group.label &&
    // A motion-carrying or markup-addressed wrapper is load-bearing for video
    // overlays: flattening it would silently drop the group's animation
    // binding (motion) or the id an external binding points at.
    !group.motion &&
    !explicitIds.has(group.id) &&
    !group.extraStyles
  );
}

function pickRoot(dom: Document): HTMLElement {
  const body = dom.body;
  if (
    body.children.length === 1 &&
    body.firstElementChild instanceof HTMLElement
  ) {
    return body.firstElementChild;
  }
  const synthetic = dom.createElement("div");
  while (body.firstChild) synthetic.appendChild(body.firstChild);
  return synthetic;
}

function parseLayer(el: Element): Layer | null {
  const tag = el.tagName.toLowerCase();

  if (tag === "img" && el instanceof HTMLImageElement) {
    return parseImage(el);
  }
  if (tag === "svg" && el instanceof SVGElement) {
    return parseSvg(el);
  }
  if (!(el instanceof HTMLElement)) return null;

  // A cropped image is an overflow:hidden frame wrapping a single offset <img>
  // (the structure decomposed bases use, and what serialize emits with a
  // data-ht-crop marker). Collapse it into one first-class cropped ImageLayer
  // so the frame and image are a single editable unit.
  const cropped = tryFlattenImageCrop(el);
  if (cropped) return cropped;

  // Agent-generated ads emit text layers as a transparent positioning wrapper
  // around an absolutely-positioned text leaf whose width/height are bogus
  // (e.g. width:100px causes "Tomorrow's design…" to wrap into a thin column).
  // Flatten the pair into one TextLayer using the wrapper's geometry, which
  // matches the layout intent.
  const flattened = tryFlattenTextWrapper(el);
  if (flattened) return flattened;

  if (isGroupContainer(el)) {
    return parseGroup(el);
  }
  if (isTextLeaf(el)) {
    return parseText(el);
  }
  return parseRect(el);
}

const HT_CROP_ATTR = "data-ht-crop";

/**
 * Collapse a crop frame — an `overflow:hidden` (or `data-ht-crop`-marked) `<div>`
 * whose subtree holds exactly one offset `<img>` and nothing else — into a single
 * `ImageLayer`. The frame becomes the layer box; the image's position (summed
 * across any trivial single-child positioning wrappers) becomes `crop`. When the
 * image instead crops via a non-centered `object-position` within a frame-sized
 * box, the collapse produces an uncropped layer that preserves the object-*
 * styles. Returns null when the structure isn't an unambiguous single-image
 * crop frame, leaving such elements to parse as groups exactly as before.
 */
function tryFlattenImageCrop(el: HTMLElement): ImageLayer | null {
  const marked = el.hasAttribute(HT_CROP_ATTR);
  // A real crop frame clips. Require the marker or an explicit overflow:hidden
  // so we never swallow a plain positioning group that happens to hold an image.
  if (!marked && !isClipped(el)) return null;

  const found = findSoleCroppedImage(el);
  if (!found) return null;
  const { img, x, y } = found;

  const width = readNumberStyle(img, "width");
  const height = readNumberStyle(img, "height");
  if (width === null || height === null) return null;

  const objectFit = (readStyle(img, "object-fit") || "cover") as ObjectFit;
  const base = parseBaseLayer(el, "image");
  // The frame normally carries the layer identity, but hand-repaired or
  // agent-authored markup may put `data-slot` on the inner <img> instead —
  // keep it either way so a save doesn't drop the template slot.
  base.slotId ??= img.getAttribute(DATA_SLOT_ATTR) ?? undefined;
  const fill = parseFillIfPresent(el);
  const rawSrc = img.getAttribute("src") ?? "";
  const dataAdRef = img.getAttribute("data-ad-ref");
  const adRef =
    dataAdRef && dataAdRef.startsWith("ad:")
      ? dataAdRef
      : rawSrc.startsWith("ad:")
        ? rawSrc
        : undefined;

  const shared: ImageLayer = {
    type: "image",
    ...base,
    // overflow is implied by the collapse: crop layers re-add it on render/
    // serialize, and an uncropped <img> clips itself via border-radius.
    extraStyles: withUnparsedFill(el, fill, stripOverflow(base.extraStyles)),
    src: rawSrc,
    assetKey: extractAssetKeyFromSrc(rawSrc),
    objectFit,
    fill,
    border: parseBorder(el),
    shadow: parseShadow(el),
    borderRadius: readBorderRadius(el),
    ...(adRef ? { adRef } : {}),
  };

  const objectPosition = readStyle(img, "object-position");
  if (isCenteredObjectPosition(objectPosition)) {
    return { ...shared, crop: { x, y, width, height } };
  }

  // A non-centered object-position means the <img> crops within its own box
  // (agent markup often frames portraits as `object-fit: cover; object-position:
  // 50% 38%` inside an equal-sized clip frame). The crop model can't represent
  // that inner offset — the draw-box is assumed to do all the cropping — and
  // collapsing to a crop layer would recenter the visible window (e.g. heads
  // shift up out of frame).
  //
  // When the image's box coincides with the frame's (the common portrait
  // framing), the frame contributes no geometric crop of its own, so collapse
  // to an *uncropped* ImageLayer instead: the layer box is the frame box and
  // `object-fit`/`object-position` keep doing the framing (`object-position`
  // rides along in extraStyles, which serialize/render inline on the <img>).
  // The 1px tolerance absorbs serializer rounding.
  const frameCoincides =
    Math.abs(x) <= 1 &&
    Math.abs(y) <= 1 &&
    Math.abs(width - base.width) <= 1 &&
    Math.abs(height - base.height) <= 1;
  if (!frameCoincides) {
    // A real geometric crop *and* a CSS-internal offset: two interacting
    // mechanisms one crop rect can't express. Leave it as a group so the img
    // keeps its own object-* styles.
    return null;
  }

  return {
    ...shared,
    extraStyles: {
      ...(shared.extraStyles ?? {}),
      "object-position": objectPosition,
    },
  };
}

/**
 * True when an `object-position` value is absent or the default center
 * (`50% 50%`), i.e. it doesn't shift the object-fit window. Unrecognized
 * values return false so `tryFlattenImageCrop` conservatively preserves them.
 */
function isCenteredObjectPosition(value: string): boolean {
  if (!value) return true;
  const parts = value.trim().toLowerCase().split(/\s+/);
  if (parts.length === 0 || parts.length > 2) return false;
  const isCenter = (p: string) => p === "center" || p === "50%";
  return parts.every(isCenter);
}

function isClipped(el: Element): boolean {
  return (
    readStyle(el, "overflow") === "hidden" ||
    readStyle(el, "overflow-x") === "hidden" ||
    readStyle(el, "overflow-y") === "hidden"
  );
}

/**
 * Descend from a crop-frame candidate to its sole `<img>`, summing the offsets
 * of any single-child positioning wrappers along the way so the returned `x/y`
 * is the image's box relative to the frame's top-left. Bails (null) on anything
 * that means this isn't a pure single-image frame: stray text, multiple
 * children, a non-image leaf, or an intermediate wrapper that paints (whose
 * styling would be lost in the collapse).
 */
function findSoleCroppedImage(
  frame: HTMLElement,
): { img: HTMLImageElement; x: number; y: number } | null {
  let cur: HTMLElement = frame;
  let x = 0;
  let y = 0;
  // Bound the descent; real frames nest only a couple of trivial wrappers.
  for (let depth = 0; depth < 6; depth += 1) {
    if (hasNonWhitespaceText(cur)) return null;
    const children = Array.from(cur.children);
    if (children.length !== 1) return null;
    const child = children[0];
    if (child instanceof HTMLImageElement) {
      return {
        img: child,
        x: x + (readNumberStyle(child, "left") ?? 0),
        y: y + (readNumberStyle(child, "top") ?? 0),
      };
    }
    // Intermediate wrapper: must be a plain positioning div, no paint of its
    // own (that would be dropped), no rotation/opacity we'd lose.
    if (
      !(child instanceof HTMLElement) ||
      child instanceof SVGElement ||
      hasVisualStyling(child) ||
      readRotation(child) !== 0 ||
      (readNumberStyle(child, "opacity") ?? 1) !== 1
    ) {
      return null;
    }
    x += readNumberStyle(child, "left") ?? 0;
    y += readNumberStyle(child, "top") ?? 0;
    cur = child;
  }
  return null;
}

function hasNonWhitespaceText(el: Element): boolean {
  for (const node of Array.from(el.childNodes)) {
    if (
      node.nodeType === Node.TEXT_NODE &&
      (node.textContent ?? "").trim().length > 0
    ) {
      return true;
    }
  }
  return false;
}

function stripOverflow(
  extraStyles: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!extraStyles) return undefined;
  const rest = { ...extraStyles };
  delete rest.overflow;
  delete rest["overflow-x"];
  delete rest["overflow-y"];
  return Object.keys(rest).length > 0 ? rest : undefined;
}

/**
 * Whether `el` holds a single text leaf and no bare text of its own: the
 * structural half of the flatten precondition (`tryFlattenTextWrapper`),
 * where the leaf's text styling wins over the wrapper's. Exported so the
 * editor's inline-edit commit (`liftFullCoverageFontSize`) decides "whole
 * layer" the same way a reload does.
 */
export function isSingleTextLeafWrapper(el: HTMLElement): boolean {
  if (el.children.length !== 1) return false;
  const child = el.firstElementChild;
  if (!(child instanceof HTMLElement)) return false;
  // Script size and offsets belong to the text run, not the containing layer.
  if (child.tagName === "SUP" || child.tagName === "SUB") {
    return false;
  }
  if (!isTextLeaf(child)) return false;
  // If the wrapper has its own non-whitespace text node children (e.g.
  // `<div>Fee-free banking<sup>1</sup></div>`), flattening would replace the
  // wrapper's content with just the inner element's innerHTML and silently
  // drop the wrapper-level text. `.children` excludes text nodes, so the
  // single-element check above doesn't catch this on its own.
  return !hasNonWhitespaceText(el);
}

/**
 * Detect "<div wrapper><div text leaf /></div>" where the wrapper carries no
 * visual styling of its own — its only purpose is to position the inner text.
 * Returns a flat TextLayer (wrapper geometry + inner font/content) or null.
 *
 * Visual styling on the wrapper (background, border, shadow, radius) means it
 * is a real layer in its own right, so we leave the structure as a group.
 */
function tryFlattenTextWrapper(el: HTMLElement): TextLayer | null {
  if (!isSingleTextLeafWrapper(el)) return null;
  const child = el.firstElementChild;
  if (!(child instanceof HTMLElement)) return null;
  if (hasVisualStyling(el)) return null;
  return parseFlattenedText(el, child);
}

/**
 * Pull the canonical first font name out of an element's `font-family` style.
 * The fallback stack is derived from this name at render/serialize time
 * (see `font-family.ts`), so the parsed multi-name stack is discarded here.
 */
function readFontFamily(el: Element): string {
  return parseFontFamilyName(readStyle(el, "font-family"));
}

const PRESERVED_WHITE_SPACE = new Set([
  "pre",
  "pre-wrap",
  "pre-line",
  "break-spaces",
]);

/**
 * Whether an element asks for its whitespace to be preserved, in which case
 * source formatting is content and must not be touched.
 */
function preservesWhitespace(el: Element): boolean {
  return (
    PRESERVED_WHITE_SPACE.has(readStyle(el, "white-space")) ||
    readStyle(el, "white-space-collapse") === "preserve"
  );
}

/**
 * A text leaf's inner HTML, minus the whitespace its source used for
 * formatting. Agent markup and SVG sources pretty-print text across lines:
 *
 *     <div style="...">
 *       minus the <span style="color:#FB4D19;">calls.</span>
 *     </div>
 *
 * A plain-browser render collapses that indentation, so it never reaches the
 * shipped pixels — but it is real text in `content`, and the editor preserves
 * whitespace on the layer under the caret, so there it paints as a leading
 * blank line and `sanitizeInlineHtml` bakes it into a `<br>` on the next save.
 *
 * A whitespace run is trimmed only against a line boundary — the leaf's own
 * edge (looking past inline ancestors), a `<br>`, or a display:block
 * neighbor. Between inline content it stays, inner span edges included: in
 * `<span>to the </span><span>aisle</span>` the boundary space is rendered.
 * NBSP is outside the character class on purpose: it is content.
 */
function trimSourceWhitespace(el: Element): string {
  if (preservesWhitespace(el)) return el.innerHTML;
  const clone = el.cloneNode(true) as Element;
  trimFormattingWhitespace(clone, clone);
  return clone.innerHTML;
}

function trimFormattingWhitespace(parent: Element, root: Element): void {
  for (const node of Array.from(parent.childNodes)) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const child = node as Element;
      if (!preservesWhitespace(child)) trimFormattingWhitespace(child, root);
      continue;
    }
    if (node.nodeType !== Node.TEXT_NODE) continue;
    let text = node.textContent ?? "";
    if (isLineBoundary(node, root, "previousSibling")) {
      text = text.replace(/^[ \t\n\r\f]+/, "");
    }
    if (isLineBoundary(node, root, "nextSibling")) {
      text = text.replace(/[ \t\n\r\f]+$/, "");
    }
    node.textContent = text;
  }
}

function isLineBoundary(
  node: ChildNode,
  root: Element,
  side: "previousSibling" | "nextSibling",
): boolean {
  let current: Node = node;
  while (true) {
    let sibling = current[side];
    while (
      sibling &&
      sibling.nodeType === Node.TEXT_NODE &&
      !/[^ \t\n\r\f]/.test(sibling.textContent ?? "")
    ) {
      sibling = sibling[side];
    }
    if (sibling) {
      if (sibling.nodeType !== Node.ELEMENT_NODE) return false;
      const el = sibling as Element;
      if (el.tagName.toLowerCase() === "br") return true;
      return readStyle(el, "display") === "block";
    }
    const parent = current.parentNode;
    if (!parent || parent === root) return true;
    if (
      parent.nodeType === Node.ELEMENT_NODE &&
      readStyle(parent as Element, "display") === "block"
    ) {
      return true;
    }
    current = parent;
  }
}

function hasVisualStyling(el: Element): boolean {
  return (
    !!readStyle(el, "background-color") ||
    !!readStyle(el, "background-image") ||
    !!readStyle(el, "background") ||
    parseBorder(el) !== null ||
    parseShadow(el).length > 0 ||
    hasRadius(readBorderRadius(el))
  );
}

function parseFlattenedText(
  wrapper: HTMLElement,
  inner: HTMLElement,
): TextLayer {
  // Text properties cascade: read inner first, fall back to wrapper. The
  // simplifier emits a typical pattern of `<div style="font-family:X;size:Y;
  // color:Z;..."><span style="font-style:italic;..."></span></div>` where
  // only the wrapper carries the actual font/size/color and the inner span
  // adds an emphasis like italic. Reading inner-only would drop font/size/
  // color into editor defaults and the line renders as 16px Inter/black.
  const inherit = (prop: string): string =>
    readStyle(inner, prop) || readStyle(wrapper, prop);
  const inheritNumber = (prop: string): number | null =>
    readNumberStyle(inner, prop) ?? readNumberStyle(wrapper, prop);

  const fontFamily = parseFontFamilyName(inherit("font-family"));
  const fontSize = inheritNumber("font-size") ?? 16;
  const fontWeight = inherit("font-weight") || "400";
  const colorRaw = inherit("color");
  const color = colorRaw ? (cssColorToHex(colorRaw) ?? colorRaw) : "#000000";
  const align = normalizeTextAlign(inherit("text-align"));
  const italic = inherit("font-style") === "italic";
  const underline = readTextDecoration(inherit, "underline");

  // Capture extras from BOTH wrapper (positioning context) and inner (text
  // styling context). Inner takes precedence on conflict because that's where
  // text-specific CSS lives.
  const base = parseBaseLayer(wrapper, "text");
  const baseExtraStyles = mergeExtraStyles(
    base.extraStyles,
    captureExtraStyles(inner, "text"),
  );
  const fill = parseFillIfPresent(inner);

  // The semantic tag may sit on the inner leaf rather than the positioning
  // wrapper (the LCM decomposer stamps it on the text div itself) — keep it
  // either way so a save doesn't drop it.
  base.layerType ??= inner.getAttribute(DATA_LAYER_TYPE_ATTR) ?? undefined;

  // Using the wrapper's box is right when the leaf's geometry is bogus (the
  // agent pattern this flatten exists for), but an auto-width leaf
  // (`width:max-content`) states its own geometry exactly: its left/top are
  // the anchor point and the translate is its grow anchor. The simplifier
  // emits this inside compensating clip wrappers (a clipped pill contains a
  // full-canvas div at a negative offset containing the leaf) — adopting the
  // wrapper's box there collapses the headline to the canvas corner and
  // drops the centering translate. Keep the leaf's geometry, re-based into
  // the wrapper's parent space, and read its anchors like parseText does.
  //
  // The simplifier also positions a rotated/translated element by wrapping its
  // subtree in a full-canvas coordinate "stage" div (see `emitTransformWrapper`
  // in dom-to-html.ts): the stage carries a canvas-origin translation — a
  // canvas-sized width/height and a negative left/top equal to `-stageOrigin` —
  // while the inner text leaf keeps its own real, canvas-absolute
  // left/top/width/height. Reading the wrapper alone re-homes the text to the
  // stage's origin (e.g. left:-1500, width:2048), detaching a badge caption
  // from its badge. So for a fixed-box leaf that carries its own non-zero
  // offset, the wrapper is a coordinate frame rather than the text's box:
  // compose the offsets (`wrapper + inner`) and take the inner leaf's real box.
  // The wrapper's rotation stays on the enclosing group, so the composed text
  // still rotates with its element. Auto-width and fixed-box leaves each need
  // the offset applied exactly once, so the two cases are mutually exclusive
  // below.
  let layoutEl = wrapperOwnsTextLayout(wrapper, inner) ? wrapper : inner;
  let resizing = readTextResizing(layoutEl);
  if (
    resizing === null &&
    wrapper.getAttribute(DATA_LAYER_TYPE_ATTR) === "text" &&
    readNumberStyle(inner, "width") === null &&
    readNumberStyle(inner, "height") === null
  ) {
    layoutEl = wrapper;
    resizing = readTextResizing(wrapper);
  }
  const verticalAlign = readVerticalAlign(layoutEl);
  const anchors = readTextAnchors(layoutEl, resizing);
  if (resizing === "auto-width") {
    if (layoutEl === inner) {
      base.x += readNumberStyle(inner, "left") ?? 0;
      base.y += readNumberStyle(inner, "top") ?? 0;
    }
    // `max-content` reads back as 0, same as parseText's auto-width path —
    // the box hugs content, so the model carries no pinned extent.
    base.width = 0;
    base.height = readNumberStyle(layoutEl, "height") ?? 0;
  } else {
    // Fixed-box leaf (e.g. a rotated badge caption with an explicit
    // width/height): when it carries its own non-zero offset, add the offsets
    // (`wrapper + inner`) and adopt the leaf's real box. Inner offset (0,0) is
    // the ordinary thin-column flatten the wrapper's geometry is meant to heal,
    // so it is left untouched.
    const innerLeft = readNumberStyle(inner, "left") ?? 0;
    const innerTop = readNumberStyle(inner, "top") ?? 0;
    if (innerLeft !== 0 || innerTop !== 0) {
      base.x += innerLeft;
      base.y += innerTop;
      base.width = readNumberStyle(inner, "width") ?? base.width;
      base.height = readNumberStyle(inner, "height") ?? base.height;
    }
  }
  const extraStyles =
    resizing === "auto-width"
      ? withoutNowrapStyles(baseExtraStyles)
      : baseExtraStyles;

  return {
    type: "text",
    ...base,
    ...(resizing === "auto-width" || resizing === "auto-height"
      ? { resizing }
      : {}),
    ...(verticalAlign ? { verticalAlign } : {}),
    ...anchors,
    extraStyles: withUnparsedFill(inner, fill, extraStyles),
    content: preservesWhitespace(wrapper)
      ? inner.innerHTML
      : trimSourceWhitespace(inner),
    fontFamily,
    fontSize,
    fontWeight,
    color,
    textAlign: align,
    lineHeight: inherit("line-height"),
    letterSpacing: inherit("letter-spacing"),
    italic,
    underline,
    ...(readTextDecoration(inherit, "line-through")
      ? { strikethrough: true }
      : {}),
    fill,
    border: parseBorder(inner),
    shadow: parseShadow(inner),
    textShadow: parseTextShadow(inner),
    borderRadius: readBorderRadius(inner),
  };
}

/**
 * Whether the flatten wrapper, not its leaf, defines the text layer's box, mode and anchors.
 * A leaf's pinned size inside an auto-height wrapper is only the box Chromium resolved for a flex item.
 */
function wrapperOwnsTextLayout(
  wrapper: HTMLElement,
  inner: HTMLElement,
): boolean {
  const innerResizing = readTextResizing(inner);
  switch (readTextResizing(wrapper)) {
    case "auto-width": {
      return innerResizing !== "auto-width";
    }
    case "auto-height": {
      const innerAtOrigin =
        (readNumberStyle(inner, "left") ?? 0) === 0 &&
        (readNumberStyle(inner, "top") ?? 0) === 0;
      return innerResizing === null && innerAtOrigin;
    }
    case "fixed":
    case null: {
      return false;
    }
  }
}

function mergeExtraStyles(
  a: Record<string, string> | undefined,
  b: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!a && !b) return undefined;
  return { ...(a ?? {}), ...(b ?? {}) };
}

function isGroupContainer(el: HTMLElement): boolean {
  // <img> and inline-text leaves are not groups.
  const tag = el.tagName.toLowerCase();
  if (TEXT_HTML_TAGS.has(tag)) return false;
  if (el.children.length === 0) return false;
  // If every child is inline phrasing content, it's still a single text leaf.
  if (isInlineSubtree(el)) return false;
  // If any child looks like its own layer (img/svg/non-inline element), this is a group.
  return Array.from(el.children).some((child) => {
    if (child instanceof HTMLImageElement) return true;
    if (child instanceof SVGElement) return true;
    if (child instanceof HTMLElement) {
      return !INLINE_HTML_TAGS.has(child.tagName.toLowerCase());
    }
    return false;
  });
}

function isTextLeaf(el: HTMLElement): boolean {
  // A template's slots are stored blank, so an empty text slot has no content
  // to infer type from. The simplifier's explicit marker is the only signal
  // left; without it the slot parses as a paint rect and loses its font,
  // line-height, and the ability to hold a value.
  if (el.getAttribute(DATA_LAYER_TYPE_ATTR) === "text") return true;
  const tag = el.tagName.toLowerCase();
  if (TEXT_HTML_TAGS.has(tag)) return true;
  if (el.children.length === 0) {
    return (el.textContent ?? "").trim().length > 0;
  }
  return isInlineSubtree(el);
}

function isInlineSubtree(el: Element): boolean {
  // `Array.from` because the project's tsconfig omits `dom.iterable` — TS
  // types HTMLCollection as array-like without `[Symbol.iterator]`, so a
  // bare `for…of el.children` fails to compile (the iteration works at
  // runtime regardless).
  for (const child of Array.from(el.children)) {
    if (!INLINE_HTML_TAGS.has(child.tagName.toLowerCase())) return false;
    if (!isInlineSubtree(child)) return false;
  }
  return true;
}

function parseRect(el: Element): RectLayer {
  const base = parseBaseLayer(el, "rect");
  const fill = parseFillIfPresent(el);
  return {
    type: "rect",
    ...base,
    extraStyles: withUnparsedFill(el, fill, base.extraStyles),
    fill,
    border: parseBorder(el),
    shadow: parseShadow(el),
    borderRadius: readBorderRadius(el),
  };
}

// captureExtraStyles strips background-image / background-color as part of
// FILL_BORDER_SHADOW_PROPS, on the assumption that parseFill captured them.
// When parseFill bails (returns null while a background is present), reattach
// the raw values here so the renderer paints them verbatim through
// extraStyles. Both properties must round-trip: an element can have a
// background-image gradient layered over a background-color base (e.g. a
// radial glow on a black canvas) — dropping the color collapses the base to
// transparent and the gradient renders against whatever is behind.
function withUnparsedFill(
  el: Element,
  fill: Fill | null,
  extraStyles: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (fill !== null) return extraStyles;
  const bgImage = readStyle(el, "background-image");
  const bgColor = readStyle(el, "background-color");
  const hasImage = bgImage && bgImage !== "none" && bgImage !== "initial";
  const hasColor =
    bgColor && bgColor !== "initial" && bgColor !== "transparent";
  if (!hasImage && !hasColor) return extraStyles;
  const out = { ...(extraStyles ?? {}) };
  if (hasImage) out["background-image"] = bgImage;
  if (hasColor) out["background-color"] = bgColor;
  return out;
}

function parseText(el: Element): TextLayer {
  const fontFamily = readFontFamily(el);
  const fontSize = readNumberStyle(el, "font-size") ?? 16;
  const fontWeight = readStyle(el, "font-weight") || "400";
  const colorRaw = readStyle(el, "color");
  const color = colorRaw ? (cssColorToHex(colorRaw) ?? colorRaw) : "#000000";
  const align = normalizeTextAlign(readStyle(el, "text-align"));
  const verticalAlign = readVerticalAlign(el);
  const italic = readStyle(el, "font-style") === "italic";
  const underline = readTextDecoration(
    (prop) => readStyle(el, prop),
    "underline",
  );

  const base = parseBaseLayer(el, "text");
  const fill = parseFillIfPresent(el);
  // A line clamp is auto-height capped at N lines: the box hugs the clamped
  // content (cut at a line boundary, with ellipsis) instead of a pinned
  // height slicing glyphs. Legacy clamped markup that also pinned a height
  // (the flatten used to emit both) coerces here; serialize canonicalizes.
  const clampLines = readMaxLines(el);
  let resizing = readTextResizing(el);
  if (clampLines && resizing !== "auto-width") resizing = "auto-height";
  const maxLines = resizing === "auto-height" ? clampLines : null;
  const { anchorX, anchorY } = readTextAnchors(el, resizing);
  const extraStyles = withUnparsedFill(
    el,
    fill,
    resizing === "auto-width"
      ? withoutNowrapStyles(base.extraStyles)
      : base.extraStyles,
  );
  return {
    type: "text",
    ...base,
    ...(resizing ? { resizing } : {}),
    ...(anchorX ? { anchorX } : {}),
    ...(anchorY ? { anchorY } : {}),
    ...(maxLines ? { maxLines } : {}),
    extraStyles,
    content: trimSourceWhitespace(el),
    fontFamily,
    fontSize,
    fontWeight,
    color,
    textAlign: align,
    ...(verticalAlign ? { verticalAlign } : {}),
    lineHeight: readStyle(el, "line-height"),
    letterSpacing: readStyle(el, "letter-spacing"),
    italic,
    underline,
    ...(readTextDecoration((prop) => readStyle(el, prop), "line-through")
      ? { strikethrough: true }
      : {}),
    fill,
    border: parseBorder(el),
    shadow: parseShadow(el),
    textShadow: parseTextShadow(el),
    borderRadius: readBorderRadius(el),
  };
}

/**
 * Whether the styles declare a text decoration. Reads the shorthand *and* the
 * `text-decoration-line` longhand: real browsers reconstitute the shorthand
 * getter from the longhand, but the backend CSSOM (`cssstyle`) does not, so
 * a `text-decoration-line: underline` headline (what the LCM simplifier
 * emits) would lose its underline on the jsdom parse only.
 */
function readTextDecoration(
  read: (prop: string) => string,
  decoration: "underline" | "line-through",
): boolean {
  const raw = read("text-decoration") || read("text-decoration-line") || "";
  return raw.split(/\s+/).includes(decoration);
}

/**
 * Normalize to the model's `TextAlign` union. The flatten forwards *computed*
 * styles, so unset alignment arrives as the logical `start` (and explicit
 * `end` appears too) — resolve them for the LTR markup the agents author.
 * An out-of-union value would leave the editor's Align control blank.
 */
function normalizeTextAlign(raw: string): TextAlign {
  if (raw === "center" || raw === "right" || raw === "justify") return raw;
  if (raw === "end") return "right";
  return "left";
}

/**
 * Infer the text resizing mode from the emitted geometry (see `TextResizing`
 * in model.ts). Parse runs without layout so it never *chooses* a mode — it
 * only recognizes what the flatten emitted. An aligned or clipped box is fixed;
 * other pinned width+height boxes are legacy layers (no mode).
 */
function readTextResizing(el: Element): TextResizing | null {
  const width = readStyle(el, "width");
  if (width === "max-content") return "auto-width";
  if (!width) return null;
  if (!readStyle(el, "height")) return "auto-height";
  if (readVerticalAlign(el)) return "fixed";
  const clips =
    readStyle(el, "overflow") === "hidden" ||
    readStyle(el, "overflow-x") === "hidden" ||
    readStyle(el, "overflow-y") === "hidden" ||
    // Logical longhands — what modern Chrome may serialize `overflow` to.
    readStyle(el, "overflow-block") === "hidden" ||
    readStyle(el, "overflow-inline") === "hidden";
  return clips ? "fixed" : null;
}

function readVerticalAlign(el: Element): TextVerticalAlign | undefined {
  switch (readStyle(el, "align-content")) {
    case "start":
      return "top";
    case "center":
      return "center";
    case "end":
      return "bottom";
    default:
      return undefined;
  }
}

/** The line-clamp line count, or null when absent/invalid. */
function readMaxLines(el: Element): number | null {
  const raw = readStyle(el, "-webkit-line-clamp");
  if (!raw || raw === "none") return null;
  const lines = Number(raw);
  return Number.isInteger(lines) && lines > 0 ? lines : null;
}

/**
 * Read grow anchors only on hugged axes: both for auto-width, vertical for auto-height.
 * Fixed and legacy layers do not interpret translations as grow anchors.
 */
function readTextAnchors(
  el: Element,
  resizing: TextResizing | null,
): { anchorX?: TextAnchorX; anchorY?: TextAnchorY } {
  if (!resizing || resizing === "fixed") return {};
  const transform = readStyle(el, "transform");
  if (!transform) return {};
  const anchors: { anchorX?: TextAnchorX; anchorY?: TextAnchorY } = {};
  if (resizing === "auto-width") {
    if (transform.includes("translateX(-50%)")) anchors.anchorX = "center";
    else if (transform.includes("translateX(-100%)")) anchors.anchorX = "right";
  }
  if (transform.includes("translateY(-50%)")) anchors.anchorY = "middle";
  else if (transform.includes("translateY(-100%)")) anchors.anchorY = "bottom";
  return anchors;
}

/**
 * Drop the no-wrap declarations from an auto-width layer's extraStyles —
 * serialize re-emits `white-space:nowrap` from the resizing mode, so leaving
 * them would duplicate the declaration (and modern Chrome's CSSOM splits
 * `white-space` into `text-wrap-mode`/`white-space-collapse` longhands, which
 * would fight a later mode change in the editor). The editor also applies it
 * when switching a layer to auto-height, whose whole point is to wrap.
 */
export function withoutNowrapStyles(
  extraStyles: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!extraStyles) return undefined;
  const out = { ...extraStyles };
  if (out["white-space"] === "nowrap" || out["white-space"] === "pre") {
    // `pre` also preserves breaks/spaces — keep that component as the
    // longhand so serialize (which re-emits only the nowrap component)
    // doesn't collapse authored line breaks. A real browser's CSSOM already
    // enumerates the longhands, so this only matters for the jsdom/cssstyle
    // parse where the shorthand survives whole.
    if (out["white-space"] === "pre" && !out["white-space-collapse"]) {
      out["white-space-collapse"] = "preserve";
    }
    delete out["white-space"];
  }
  if (out["text-wrap-mode"] === "nowrap") delete out["text-wrap-mode"];
  if (out["text-wrap"] === "nowrap") delete out["text-wrap"];
  if (out["white-space-collapse"] === "collapse") {
    delete out["white-space-collapse"];
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function parseImage(el: HTMLImageElement): ImageLayer {
  const objectFit = (readStyle(el, "object-fit") || "cover") as ObjectFit;
  const base = parseBaseLayer(el, "image");
  const fill = parseFillIfPresent(el);
  const rawSrc = el.getAttribute("src") ?? "";
  const dataAdRef = el.getAttribute("data-ad-ref");
  const adRef =
    dataAdRef && dataAdRef.startsWith("ad:")
      ? dataAdRef
      : rawSrc.startsWith("ad:")
        ? rawSrc
        : undefined;
  return {
    type: "image",
    ...base,
    extraStyles: withUnparsedFill(el, fill, base.extraStyles),
    src: rawSrc,
    assetKey: extractAssetKeyFromSrc(rawSrc),
    objectFit,
    fill,
    border: parseBorder(el),
    shadow: parseShadow(el),
    borderRadius: readBorderRadius(el),
    ...(adRef ? { adRef } : {}),
  };
}

/**
 * Pull the S3 key out of a `/agents/files?key=<…>` URL. `resolve-ad-refs`
 * rewrites the original `ad:<filename>` ref into this form at mount time.
 * Returns null for external URLs or any src we can't parse.
 */
function extractAssetKeyFromSrc(src: string): string | null {
  if (!src) return null;
  const qIdx = src.indexOf("?");
  if (qIdx < 0) return null;
  try {
    return new URLSearchParams(src.slice(qIdx + 1)).get("key") || null;
  } catch {
    return null;
  }
}

function parseSvg(el: SVGElement): SvgLayer {
  const base = parseBaseLayer(el, "svg");
  const viewBox = el.getAttribute("viewBox") ?? "";
  // `svg-to-html` emits its fallback <svg> with width/height as SVG
  // presentation *attributes* (`width="1080" height="1350"`), not inline
  // style. parseBaseLayer only reads style, so it falls back to 0 — and a
  // zero-sized <svg> that carries a viewBox scales its content to nothing,
  // hiding the layer in the editor. Worse, serializeSvg would then persist
  // `width:0px;height:0px`, breaking the launch download too. Fall back to the
  // attributes, then to the viewBox extent, so the layer keeps its real size.
  const width =
    base.width || readNumberAttr(el, "width") || viewBoxExtent(viewBox, 2);
  const height =
    base.height || readNumberAttr(el, "height") || viewBoxExtent(viewBox, 3);
  return {
    type: "svg",
    ...base,
    width,
    height,
    innerMarkup: el.innerHTML,
    viewBox,
  };
}

function readNumberAttr(el: Element, attr: string): number | null {
  const raw = el.getAttribute(attr);
  if (!raw) return null;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

// `viewBox` is `min-x min-y width height`; index 2/3 pull width/height.
function viewBoxExtent(viewBox: string, index: 2 | 3): number {
  const parts = viewBox.trim().split(/[\s,]+/);
  if (parts.length !== 4) return 0;
  const n = Number.parseFloat(parts[index]!);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function parseGroup(el: HTMLElement): Layer {
  const children = Array.from(el.children)
    .map((child) => parseLayer(child))
    .filter((layer): layer is Layer => layer !== null);
  return {
    type: "group",
    ...parseBaseLayer(el, "group"),
    children,
  };
}

function parseBaseLayer(
  el: Element,
  kind: LayerKind,
): {
  id: string;
  slotId?: string;
  layerType?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  flipX?: boolean;
  flipY?: boolean;
  opacity: number;
  hidden: boolean;
  locked: boolean;
  label?: string;
  motion?: LayerMotion;
  extraStyles?: Record<string, string>;
} {
  const existingId = el.getAttribute(HT_ID_ATTR);
  const slotId = el.getAttribute(DATA_SLOT_ATTR);
  const layerType = el.getAttribute(DATA_LAYER_TYPE_ATTR);
  const motion = readMotion(el);
  const box = readLayerBox(el, kind);
  let extraStyles = captureExtraStyles(el, kind);
  // Keep paint scaled together (text, borders, filters and descendants).
  // Ungrouping transfers the group's scale onto each promoted child.
  const scale = box.didFoldScale ? null : readLayerScaleTransform(el, kind);
  if (scale) extraStyles = { ...extraStyles, transform: scale };
  if (box.didFoldScale && extraStyles) {
    delete extraStyles["transform-origin"];
  }
  return {
    id: existingId ?? "",
    ...(slotId ? { slotId } : {}),
    ...(layerType ? { layerType } : {}),
    x: box.x,
    y: box.y,
    width: box.width,
    height: box.height,
    rotation: readRotation(el),
    ...readFlip(el),
    opacity: readNumberStyle(el, "opacity") ?? 1,
    hidden: readStyle(el, "display") === "none",
    locked: el.hasAttribute(HT_LOCKED_ATTR),
    label: el.getAttribute(HT_LABEL_ATTR) ?? undefined,
    ...(motion ? { motion } : {}),
    extraStyles:
      extraStyles && Object.keys(extraStyles).length > 0
        ? extraStyles
        : undefined,
  };
}

/**
 * Read `data-ht-motion` into `layer.motion`. The attribute is agent-authored
 * and untyped, so anything malformed — bad JSON, wrong shape, blocks the
 * renderer could not evaluate — is dropped rather than thrown, keeping the
 * rest of the layer loadable. Empty block lists collapse to "no motion",
 * which is also what the serializer emits, so the round trip stays a fixed
 * point.
 */
function readMotion(el: Element): LayerMotion | undefined {
  const raw = el.getAttribute(HT_MOTION_ATTR);
  if (!raw) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (typeof parsed !== "object" || parsed === null) return undefined;
  const blocks = parseAnimationBlocks((parsed as { blocks?: unknown }).blocks);
  return blocks.length > 0 ? { blocks } : undefined;
}

/**
 * Pull every inline-style declaration the parser doesn't already model into a
 * record so the serializer/renderer can reapply it. We always force
 * `position: absolute` and `display:none` is the modeled `hidden` flag — those
 * never leak through. Unknown `display` values (`flex`, `grid`) and any
 * arbitrary CSS (filter, padding, white-space, custom properties, …) flow
 * through here and survive the round-trip.
 */
function captureExtraStyles(
  el: Element,
  kind: LayerKind,
): Record<string, string> | undefined {
  if (!(el instanceof HTMLElement || el instanceof SVGElement))
    return undefined;
  const out: Record<string, string> = {};
  const style = el.style;
  const seen = new Set<string>();
  for (let i = 0; i < style.length; i += 1) {
    const prop = style.item(i);
    seen.add(prop);
    const value = style.getPropertyValue(prop);
    if (isExtraStyleProp(prop, value, kind)) out[prop] = value;
  }
  // Recover declarations the CSSOM silently dropped. `cssstyle` (the CSSOM
  // behind jsdom/linkedom that backs this parse) discards properties it doesn't
  // recognize — most notably `text-wrap-mode`, which the simplifier emits for
  // `white-space: nowrap` headlines (modern Chrome serializes the `white-space`
  // shorthand to that longhand). A real browser — the in-editor parse — keeps
  // it, so without this fallback the backend Doc diverges from the editor: a
  // no-wrap headline loses its `text-wrap-mode: nowrap` and reflows to multiple
  // lines on export / Figma-plugin render. Mirrors the raw-attribute fallback
  // in `readStyle`. Allowlisted so the naive `;`-split can't surface garbage
  // from values that legitimately contain semicolons (e.g. `data:` URLs).
  for (const [prop, value] of parseRawStyleDeclarations(el)) {
    if (seen.has(prop)) continue;
    if (!CSSOM_DROPPED_RECOVERABLE.has(prop)) continue;
    if (isExtraStyleProp(prop, value, kind)) out[prop] = value;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

// Whether an inline declaration should be preserved in `extraStyles` — i.e. it
// is not already modeled as a typed layer field, nor force-set on render
// (`position`), nor represented by the `hidden` flag (`display:none`). Shared by
// the CSSOM pass and the raw-attribute recovery pass so both apply identical
// consume-set rules.
function isExtraStyleProp(
  prop: string,
  value: string,
  kind: LayerKind,
): boolean {
  if (POSITION_BASE_PROPS.has(prop)) return false;
  // `display:none` is modeled as `hidden`; other display values fall through.
  if (prop === "display" && value === "none") return false;
  // Groups/svgs only model position+opacity. Everything else passes through.
  if (kind === "group" || kind === "svg") return true;
  if (FILL_BORDER_SHADOW_PROPS.has(prop)) return false;
  if (kind === "text" && TEXT_PROPS.has(prop)) return false;
  if (
    kind === "text" &&
    prop === "align-content" &&
    ["start", "center", "end"].includes(value)
  )
    return false;
  if (kind === "text" && TEXT_CLAMP_PROPS.has(prop)) return false;
  // The editor's Figma-style wrap semantic: TextView renders it and
  // serialize.ts re-emits it canonically for wrapping modes, so a stored
  // `word-break` must not ride through extraStyles and duplicate.
  if (kind === "text" && prop === "word-break") return false;
  // The clamp kit's display value is emitted from `maxLines` too.
  if (kind === "text" && prop === "display" && value === "-webkit-box") {
    return false;
  }
  if (kind === "image" && IMAGE_PROPS.has(prop)) return false;
  return true;
}

// Enumerate the raw inline `style` attribute as lower-cased [prop, value] pairs,
// including declarations the CSSOM dropped. The split is naive (`;`/`:`), which
// is fine for the allowlisted keyword-valued properties it recovers.
function parseRawStyleDeclarations(el: Element): Array<[string, string]> {
  const raw = el.getAttribute("style");
  if (!raw) return [];
  const out: Array<[string, string]> = [];
  for (const decl of raw.split(";")) {
    const colon = decl.indexOf(":");
    if (colon < 0) continue;
    const prop = decl.slice(0, colon).trim().toLowerCase();
    const value = decl.slice(colon + 1).trim();
    if (prop && value) out.push([prop, value]);
  }
  return out;
}

const CSS_WIDE_KEYWORDS = new Set([
  "initial",
  "inherit",
  "unset",
  "revert",
  "revert-layer",
]);

/**
 * `readStyle`, but CSS-wide keywords (`initial`, `inherit`, ...) count as absent.
 * A shorthand resets its longhands, so `background: linear-gradient(...)` makes
 * Chrome's CSSOM report `background-color: initial` — truthy, so a longhand-first
 * lookup would stop there and record `"initial"` instead of cascading through to the
 * shorthand holding the gradient, which serializes back out as transparent
 */
function readEffectiveStyle(el: Element, prop: string): string | null {
  const value = readStyle(el, prop);
  if (!value || CSS_WIDE_KEYWORDS.has(value.toLowerCase())) return null;
  return value;
}

export function readStyle(el: Element, prop: string): string {
  if (!(el instanceof HTMLElement || el instanceof SVGElement)) return "";
  const value = el.style.getPropertyValue(prop).trim();
  if (value) return value;
  // jsdom's `cssstyle` silently drops declarations whose values it doesn't
  // fully recognize — most notably `linear-gradient`/`radial-gradient` on
  // `background-image`, but also other modern CSS values. Fall back to the
  // raw `style` attribute when the CSSStyleDeclaration came back empty so
  // the parser can still see those values. A real browser doesn't need this
  // fallback (CSS support is complete); it's a no-op there.
  return readStyleAttributeProp(el, prop);
}

const IMPORTANT_FLAG = /\s*!\s*important\s*$/i;

function readStyleAttributeProp(el: Element, prop: string): string {
  const raw = el.getAttribute("style");
  if (!raw) return "";
  const target = prop.toLowerCase();
  // Cascade order within the one attribute: `!important` outranks a normal declaration
  // however late it comes, and among equals the last wins.
  let found = "";
  let foundIsImportant = false;
  for (const decl of raw.split(";")) {
    const colon = decl.indexOf(":");
    if (colon < 0) continue;
    if (decl.slice(0, colon).trim().toLowerCase() !== target) continue;
    const declared = decl.slice(colon + 1).trim();
    const isImportant = IMPORTANT_FLAG.test(declared);
    if (foundIsImportant && !isImportant) continue;
    // Stripped, so this path reports what `getPropertyValue` would.
    const value = declared.replace(IMPORTANT_FLAG, "").trim();
    // A flag with nothing in front of it is not a declaration, so it must not shadow a later one.
    if (!value) continue;
    found = value;
    foundIsImportant = isImportant;
  }
  return found;
}

function readNumberStyle(el: Element, prop: string): number | null {
  const raw = readStyle(el, prop);
  if (!raw) return null;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

function readPxStyle(el: Element, prop: string): number | null {
  return readNumberStyle(el, prop);
}

// Read border-radius from a parsed (detached) DOM element. Models only
// circular per-corner radii — elliptical (`50px 30px`) collapses to the
// smaller axis. Percentage values can't be resolved without rendered
// dimensions; the simplifier upstream is responsible for converting them to
// px before the markup lands here, so a stray `%` drops its unit.
function readBorderRadius(el: Element): BorderRadius {
  const shorthand = readStyle(el, "border-radius");
  const tlRaw = readStyle(el, "border-top-left-radius");
  const trRaw = readStyle(el, "border-top-right-radius");
  const brRaw = readStyle(el, "border-bottom-right-radius");
  const blRaw = readStyle(el, "border-bottom-left-radius");

  if (tlRaw || trRaw || brRaw || blRaw) {
    const fallback = shorthand ? parseCornerValue(shorthand) : 0;
    return normalizeRadius({
      tl: tlRaw ? parseCornerValue(tlRaw) : fallback,
      tr: trRaw ? parseCornerValue(trRaw) : fallback,
      br: brRaw ? parseCornerValue(brRaw) : fallback,
      bl: blRaw ? parseCornerValue(blRaw) : fallback,
    });
  }

  return shorthand ? parseRadiusShorthand(shorthand) : 0;
}

// One corner value: `Npx` (circular), `Npx Mpx` (elliptical → preserve both
// axes), or a stray `%` (drop the unit, treat as circular). Returns a
// non-negative px corner radius.
function parseCornerValue(raw: string): CornerRadius {
  const nums = raw
    .trim()
    .split(/\s+/)
    .map((p) => Number.parseFloat(p))
    .filter(Number.isFinite);
  if (nums.length === 0) return 0;
  if (nums.length === 1) return Math.max(0, nums[0]!);
  return makeCorner(Math.max(0, nums[0]!), Math.max(0, nums[1]!));
}

// Parse `border-radius` shorthand including 1/2/3/4-value lists and the
// elliptical slash form (`tl tr br bl / tl' tr' br' bl'`). The slash form
// preserves each corner's elliptical axes.
function parseRadiusShorthand(raw: string): BorderRadius {
  const [horizPart, vertPart] = raw
    .trim()
    .split("/")
    .map((s) => s.trim());
  const horiz = expandRadiusList(horizPart ?? "");
  const vert = vertPart ? expandRadiusList(vertPart) : horiz;
  return normalizeRadius({
    tl: makeCorner(horiz.tl, vert.tl),
    tr: makeCorner(horiz.tr, vert.tr),
    br: makeCorner(horiz.br, vert.br),
    bl: makeCorner(horiz.bl, vert.bl),
  });
}

function makeCorner(rx: number, ry: number): CornerRadius {
  return rx === ry ? rx : { rx, ry };
}

// Expand a 1/2/3/4-value list to four corners per CSS spec.
function expandRadiusList(input: string): {
  tl: number;
  tr: number;
  br: number;
  bl: number;
} {
  const nums = input
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => Math.max(0, Number.parseFloat(p) || 0));
  if (nums.length === 0) return { tl: 0, tr: 0, br: 0, bl: 0 };
  if (nums.length === 1) {
    const v = nums[0]!;
    return { tl: v, tr: v, br: v, bl: v };
  }
  if (nums.length === 2) {
    return { tl: nums[0]!, tr: nums[1]!, br: nums[0]!, bl: nums[1]! };
  }
  if (nums.length === 3) {
    return { tl: nums[0]!, tr: nums[1]!, br: nums[2]!, bl: nums[1]! };
  }
  return { tl: nums[0]!, tr: nums[1]!, br: nums[2]!, bl: nums[3]! };
}

const ROTATE_RE = /rotate\(\s*(-?[\d.]+)deg\s*\)/i;

function readRotation(el: Element): number {
  const transform = readStyle(el, "transform");
  if (!transform) return 0;
  const match = ROTATE_RE.exec(transform);
  return match ? Number.parseFloat(match[1]!) : 0;
}

const SCALE_X_RE = /scalex\(\s*(-?[\d.]+)\s*\)/i;
const SCALE_Y_RE = /scaley\(\s*(-?[\d.]+)\s*\)/i;
// `scale(` never matches inside `scaleX(`/`scaleY(` — the paren must follow
// "scale" directly.
const SCALE_RE = /\bscale\(\s*(-?[\d.]+)\s*(?:,\s*(-?[\d.]+)\s*)?\)/i;
const CSS_NUMBER_PATTERN = String.raw`[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?`;
const CSS_SCALE_FACTOR_PATTERN = String.raw`${CSS_NUMBER_PATTERN}%?`;
const PURE_SCALE_RE = new RegExp(
  String.raw`^\s*scale\(\s*(${CSS_SCALE_FACTOR_PATTERN})(?:\s*,\s*(${CSS_SCALE_FACTOR_PATTERN}))?\s*\)\s*$`,
  "i",
);
const ORIGIN_LENGTH_RE = new RegExp(
  String.raw`^(${CSS_NUMBER_PATTERN})(px|%)?$`,
  "i",
);
const SCALE_INVARIANT_IMAGE_PROPS = new Set([
  ...POSITION_BASE_PROPS,
  "box-sizing",
  "font-feature-settings",
  "object-fit",
  "object-position",
  "transform-origin",
]);
const ORIGIN_X_KEYWORDS: Record<string, number> = {
  left: 0,
  center: 0.5,
  right: 1,
};
const ORIGIN_Y_KEYWORDS: Record<string, number> = {
  top: 0,
  center: 0.5,
  bottom: 1,
};
const OBJECT_POSITION_KEYWORDS = new Set([
  ...Object.keys(ORIGIN_X_KEYWORDS),
  ...Object.keys(ORIGIN_Y_KEYWORDS),
]);
const PERCENTAGE_RE = new RegExp(String.raw`^${CSS_NUMBER_PATTERN}%$`, "i");
const SCALE_INVARIANT_OBJECT_FITS = new Set(["contain", "cover", "fill"]);

function readLayerBox(
  el: Element,
  kind: LayerKind,
): {
  x: number;
  y: number;
  width: number;
  height: number;
  didFoldScale: boolean;
} {
  const x = readNumberStyle(el, "left") ?? 0;
  const y = readNumberStyle(el, "top") ?? 0;
  const width = readNumberStyle(el, "width") ?? 0;
  const height = readNumberStyle(el, "height") ?? 0;
  const box = { x, y, width, height, didFoldScale: false };
  if (
    kind !== "image" ||
    !(el instanceof HTMLImageElement) ||
    !hasScaleInvariantImageStyles(el)
  ) {
    return box;
  }

  const scale = parsePositiveUniformScale(readStyle(el, "transform"));
  if (scale === null) return box;

  const origin = readScaleOrigin(el, width, height);
  if (!origin) return box;

  return {
    x: x + origin.x * (1 - scale),
    y: y + origin.y * (1 - scale),
    width: width * scale,
    height: height * scale,
    didFoldScale: true,
  };
}

function hasScaleInvariantImageStyles(el: HTMLImageElement): boolean {
  for (let index = 0; index < el.style.length; index += 1) {
    if (!SCALE_INVARIANT_IMAGE_PROPS.has(el.style.item(index))) return false;
  }
  const objectFit = readStyle(el, "object-fit") || "cover";
  if (!SCALE_INVARIANT_OBJECT_FITS.has(objectFit)) return false;
  const objectPosition = readStyle(el, "object-position");
  return (
    !objectPosition ||
    objectPosition
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .every(
        (token) =>
          OBJECT_POSITION_KEYWORDS.has(token) || PERCENTAGE_RE.test(token),
      )
  );
}

function parsePositiveUniformScale(transform: string | null): number | null {
  if (!transform) return null;
  const match = PURE_SCALE_RE.exec(transform);
  if (!match) return null;
  const scale = parseScaleFactor(match[1]!);
  const yScale = match[2] === undefined ? scale : parseScaleFactor(match[2]);
  if (!Number.isFinite(scale) || scale <= 0) return null;
  return yScale === scale ? scale : null;
}

function readLayerScaleTransform(el: Element, kind: LayerKind): string | null {
  const transform = readStyle(el, "transform");
  if (!transform) return null;
  // Rotation and flips are modeled separately and commute with a uniform scale.
  const anchors =
    kind === "text" ? readTextAnchors(el, readTextResizing(el)) : {};
  let unanchored = transform;
  if (anchors.anchorX)
    unanchored = unanchored.replace(
      `translateX(-${anchors.anchorX === "center" ? 50 : 100}%)`,
      "",
    );
  if (anchors.anchorY)
    unanchored = unanchored.replace(
      `translateY(-${anchors.anchorY === "middle" ? 50 : 100}%)`,
      "",
    );
  const residual = unanchored
    .replace(ROTATE_RE, "")
    .replace(/scalex\(\s*-1\s*\)/i, "")
    .replace(/scaley\(\s*-1\s*\)/i, "")
    .replace(
      new RegExp(SCALE_RE.source, "gi"),
      (match, x: string, y?: string) => {
        const sx = Number(x);
        const sy = y === undefined ? sx : Number(y);
        return Math.abs(sx) === 1 && Math.abs(sy) === 1 && (sx < 0 || sy < 0)
          ? ""
          : match;
      },
    );
  const scale = parsePositiveUniformScale(residual);
  return scale === null ? null : `scale(${scale})`;
}

function parseScaleFactor(value: string): number {
  const numeric = value.endsWith("%") ? value.slice(0, -1) : value;
  const scale = Number(numeric);
  return value.endsWith("%") ? scale / 100 : scale;
}

function readScaleOrigin(
  el: Element,
  width: number,
  height: number,
): { x: number; y: number } | null {
  const transformOrigin = readStyle(el, "transform-origin");
  if (!transformOrigin) return { x: width / 2, y: height / 2 };
  const tokens = transformOrigin.trim().toLowerCase().split(/\s+/);
  if (tokens.length < 1 || tokens.length > 2) return null;
  const [first, second = "center"] = tokens;
  if (!first) return null;
  const firstIsVertical =
    Object.hasOwn(ORIGIN_Y_KEYWORDS, first) &&
    !Object.hasOwn(ORIGIN_X_KEYWORDS, first);
  const secondIsHorizontal =
    Object.hasOwn(ORIGIN_X_KEYWORDS, second) &&
    !Object.hasOwn(ORIGIN_Y_KEYWORDS, second);
  const xToken = firstIsVertical || secondIsHorizontal ? second : first;
  const yToken = firstIsVertical || secondIsHorizontal ? first : second;
  const x = resolveOriginAxis(xToken, width, ORIGIN_X_KEYWORDS);
  const y = resolveOriginAxis(yToken, height, ORIGIN_Y_KEYWORDS);
  return x === null || y === null ? null : { x, y };
}

function resolveOriginAxis(
  token: string,
  extent: number,
  keywords: Record<string, number>,
): number | null {
  if (Object.hasOwn(keywords, token)) return (keywords[token] ?? 0) * extent;
  const match = ORIGIN_LENGTH_RE.exec(token);
  if (!match) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value)) return null;
  if (match[2]?.toLowerCase() === "%") return (value / 100) * extent;
  return match[2]?.toLowerCase() === "px" || value === 0 ? value : null;
}

/**
 * Negative scale factors in the transform read as flips. The serializer emits
 * `scaleX(-1)`/`scaleY(-1)`; agent-authored markup may use `scale(-1)` or
 * `scale(-1, 1)` forms, so those are folded in too.
 */
function readFlip(el: Element): { flipX?: boolean; flipY?: boolean } {
  const transform = readStyle(el, "transform");
  if (!transform) return {};
  const sx = SCALE_X_RE.exec(transform);
  const sy = SCALE_Y_RE.exec(transform);
  let flipX = sx !== null && Number.parseFloat(sx[1]!) < 0;
  let flipY = sy !== null && Number.parseFloat(sy[1]!) < 0;
  for (const s of transform.matchAll(new RegExp(SCALE_RE.source, "gi"))) {
    const first = Number.parseFloat(s[1]!);
    // One-argument scale() applies to both axes.
    const second = s[2] !== undefined ? Number.parseFloat(s[2]) : first;
    if (first < 0) flipX = !flipX;
    if (second < 0) flipY = !flipY;
  }
  return {
    ...(flipX ? { flipX: true } : {}),
    ...(flipY ? { flipY: true } : {}),
  };
}

function parseFillIfPresent(el: Element): Fill | null {
  const hasBg =
    !!readStyle(el, "background-color") ||
    !!readStyle(el, "background-image") ||
    !!readStyle(el, "background");
  return hasBg ? parseFill(el) : null;
}

// Returns null when `background-image` is set but the gradient form isn't
// representable in the Fill model (e.g. multi-layer or a radial with shape /
// position prefix). The caller routes the raw CSS value through extraStyles
// in that case so the renderer paints it verbatim.
function parseFill(el: Element): Fill | null {
  // Where the gradient could be hiding:
  //   - `background-image` — what real browsers expose after shorthand
  //     expansion. jsdom's cssstyle doesn't expand `background:`, so this
  //     slot is often empty even when the source has a gradient.
  //   - `background` shorthand — the raw-style-attribute fallback in
  //     readStyle returns the entire shorthand value here. When the source
  //     wrote `background: linear-gradient(...)`, this is where we find it.
  // `background-image: initial` (Chromium's shorthand-expansion fallback)
  // is treated as no image so we fall through to color parsing — both
  // gradient parsers reject the literal string "initial".
  const bgImage = readStyle(el, "background-image");
  const bgShorthand = readStyle(el, "background");
  let gradientCandidate = "";
  if (bgImage && bgImage !== "initial" && bgImage !== "none") {
    gradientCandidate = bgImage;
  } else if (isGradientValue(bgShorthand)) {
    gradientCandidate = bgShorthand;
  }
  if (gradientCandidate) {
    const width = readPxStyle(el, "width");
    const height = readPxStyle(el, "height");
    const box = width !== null && height !== null ? { width, height } : null;
    const linear = parseLinearGradient(gradientCandidate, box);
    if (linear) return linear;
    const radial = parseRadialGradient(gradientCandidate, box);
    if (radial) return radial;
    return null;
  }
  const colorRaw =
    readStyle(el, "background-color") || readStyle(el, "background");
  return {
    kind: "solid",
    color: colorRaw ? (cssColorToHex(colorRaw) ?? colorRaw) : "#ffffff",
    angle: 0,
    stops: [],
  };
}

// Hex, rgb()/hsl() or a named color: what a background holds when it's a plain color.
const BACKGROUND_COLOR_RE = /^(#[0-9a-f]{3,8}|(?:rgb|hsl)a?\([^)]*\)|[a-z]+)$/i;

/**
 * Reads a CSS `background` value, like the doc root's, as a Fill: a color, or a linear or radial gradient the model can hold.
 * Returns null for anything else, such as `none`, an image, or a radial gradient with its own shape or position.
 */
export function parseBackgroundFill(
  value: string,
  box: GradientBox = null,
): Fill | null {
  const trimmed = value.trim();
  if (isGradientValue(trimmed)) {
    const gradient =
      parseLinearGradient(trimmed, box) ?? parseRadialGradient(trimmed, box);
    // The value carries no latent solid color, so switching to solid starts from the first stop.
    return (
      gradient && {
        ...gradient,
        color: gradient.stops[0]?.color ?? gradient.color,
      }
    );
  }
  if (trimmed.toLowerCase() === "none" || !BACKGROUND_COLOR_RE.test(trimmed)) {
    return null;
  }
  return {
    kind: "solid",
    color: cssColorToHex(trimmed) ?? trimmed,
    angle: 0,
    stops: [],
  };
}

// True for inline-style values whose first token is a CSS gradient function.
// Used to route a `background:` shorthand value to the gradient parsers
// instead of letting it land in the solid-fill color slot.
function isGradientValue(value: string): boolean {
  return /^\s*(linear|radial|conic)-gradient\s*\(/i.test(value);
}

const LINEAR_RE = /^linear-gradient\((.*)\)$/s;
const RADIAL_RE = /^radial-gradient\((.*)\)$/s;
const ANGLE_RE = /^(-?[\d.]+)deg$/;
// Also match explicit size prefixes (e.g. `460px 760px at 0% 58%`). Valid
// color stops never start with a length unit — they're `<color>` or
// `<color> <offset>` — so a leading number is unambiguously a sized radial,
// not a stop.
const RADIAL_PREFIX =
  /^(circle|ellipse|closest|farthest|at\s|-?[\d.]+(px|%|em|rem|vw|vh|vmin|vmax))/i;
const STOP_RE =
  /^(#[0-9a-f]+|rgba?\([^)]+\)|hsla?\([^)]+\)|[a-z]+)\s*(?:(-?[\d.]+)(%|px)?)?$/i;

/** The px box a gradient paints, which length stops resolve against; null when it isn't sized in px. */
type GradientBox = { width: number; height: number } | null;

// A `to <side>` direction as the angle it stands for.
const SIDE_ANGLES: ReadonlyMap<string, number> = new Map([
  ["top", 0],
  ["right", 90],
  ["bottom", 180],
  ["left", 270],
]);

function parseLinearGradient(value: string, box: GradientBox): Fill | null {
  const match = LINEAR_RE.exec(value.trim());
  if (!match?.[1]) return null;
  const parts = splitTopLevel(match[1]).map((s) => s.trim());
  const first = parts[0] ?? "";
  const degrees = ANGLE_RE.exec(first)?.[1];
  const direction = /^to\s+(.+)$/i.exec(first)?.[1];
  let angle = 180;
  if (degrees !== undefined) {
    angle = Number.parseFloat(degrees);
  } else if (direction !== undefined) {
    const sideAngle = SIDE_ANGLES.get(direction.trim().toLowerCase());
    // A corner's angle depends on the box's aspect ratio, which a Fill can't hold.
    if (sideAngle === undefined) return null;
    angle = sideAngle;
  }
  const stopParts =
    degrees !== undefined || direction !== undefined ? parts.slice(1) : parts;
  // CSS measures stops along the gradient line, whose length depends on the angle.
  const radians = (angle * Math.PI) / 180;
  const lineLength = box
    ? Math.abs(box.width * Math.sin(radians)) +
      Math.abs(box.height * Math.cos(radians))
    : null;
  const stops = parseStops(stopParts, lineLength);
  if (!stops) return null;
  return { kind: "linear", color: "#000000", angle, stops };
}

function parseRadialGradient(value: string, box: GradientBox): Fill | null {
  const match = RADIAL_RE.exec(value.trim());
  if (!match?.[1]) return null;
  const parts = splitTopLevel(match[1]).map((s) => s.trim());
  const first = parts[0] ?? "";
  // The Fill model only represents the default radial shape (centered ellipse,
  // farthest-corner). Any explicit shape/size/position prefix would be lost
  // on render — bail and let the caller route the raw CSS through extraStyles.
  if (RADIAL_PREFIX.test(first)) return null;
  // The default farthest-corner ellipse measures stops along its horizontal radius, width / sqrt(2).
  const stops = parseStops(parts, box ? box.width / Math.SQRT2 : null);
  if (!stops) return null;
  return { kind: "radial", color: "#000000", angle: 0, stops };
}

type ParsedStop = GradientStop & { explicit: boolean };

// Null when any stop is unreadable, so the caller keeps the raw CSS instead of painting a guessed stop.
function parseStops(
  parts: string[],
  lineLength: number | null,
): GradientStop[] | null {
  const stops: ParsedStop[] = [];
  for (const part of parts) {
    const stop = parseStop(part, lineLength);
    if (!stop) return null;
    stops.push(stop);
  }
  return distributeStopOffsets(stops);
}

function parseStop(part: string, lineLength: number | null): ParsedStop | null {
  const match = STOP_RE.exec(part.trim());
  if (!match?.[1]) return null;
  const color = cssColorToHex(match[1]) ?? match[1];
  const [, , rawOffset, unit] = match;
  if (rawOffset === undefined) return { color, offset: 0, explicit: false };
  const value = Number.parseFloat(rawOffset);
  if (unit === "%") return { color, offset: value, explicit: true };
  // A unitless position is only valid CSS as 0.
  if (unit === undefined) {
    return value === 0 ? { color, offset: 0, explicit: true } : null;
  }
  if (!lineLength) return null;
  return { color, offset: (value / lineLength) * 100, explicit: true };
}

// Stops with no explicit position fill in per CSS spec: first stop → 0%,
// last → 100%, runs of missing positions evenly distributed between the
// surrounding anchors. Without this, `linear-gradient(red, blue)` becomes
// two stops both at 0% (instant flip), not a 0%→100% gradient.
function distributeStopOffsets(stops: ParsedStop[]): GradientStop[] {
  if (stops.length === 0) return [];
  const out = stops.map((s) => ({ ...s }));
  if (!out[0]!.explicit) {
    out[0]!.offset = 0;
    out[0]!.explicit = true;
  }
  if (!out[out.length - 1]!.explicit) {
    out[out.length - 1]!.offset = 100;
    out[out.length - 1]!.explicit = true;
  }
  let i = 0;
  while (i < out.length) {
    if (out[i]!.explicit) {
      i += 1;
      continue;
    }
    let j = i;
    while (j < out.length && !out[j]!.explicit) j += 1;
    // [i, j) is a run of missing stops between explicit anchors at i-1 and j.
    const prev = out[i - 1]!.offset;
    const next = out[j]!.offset;
    const span = j - (i - 1);
    for (let k = i; k < j; k += 1) {
      out[k]!.offset = prev + ((next - prev) * (k - (i - 1))) / span;
      out[k]!.explicit = true;
    }
    i = j;
  }
  return out.map(({ color, offset }) => ({ color, offset }));
}

export function splitTopLevel(input: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let buffer = "";
  for (const ch of input) {
    if (ch === "(") depth += 1;
    else if (ch === ")") depth -= 1;
    if (ch === "," && depth === 0) {
      out.push(buffer);
      buffer = "";
      continue;
    }
    buffer += ch;
  }
  if (buffer.trim()) out.push(buffer);
  return out;
}

function parseBorder(el: Element): Border | null {
  // Read per-physical-side from the inline style. The browser resolves
  // any shorthand or logical longhand the source used into the four
  // `border-{top,right,bottom,left}-{width,color}` values on inline style,
  // so reading the physical longhands directly is the canonical source.
  const top = readBorderSide(el, "top");
  const right = readBorderSide(el, "right");
  const bottom = readBorderSide(el, "bottom");
  const left = readBorderSide(el, "left");
  if (
    top.width <= 0 &&
    right.width <= 0 &&
    bottom.width <= 0 &&
    left.width <= 0
  ) {
    return null;
  }
  return { top, right, bottom, left };
}

// Logical longhand for each physical side (LTR; ad-studio canvases are LTR).
// The browser expands physical shorthands like `border-top:` into the per-side
// physical longhands on inline style, but does NOT cross-map logical→physical
// (e.g. `border-block-start:` only sets `border-block-start-*`, not
// `border-top-*`). Fall back to the logical equivalent if no physical value
// is present.
const PHYSICAL_TO_LOGICAL: Record<
  "top" | "right" | "bottom" | "left",
  "block-start" | "inline-end" | "block-end" | "inline-start"
> = {
  top: "block-start",
  right: "inline-end",
  bottom: "block-end",
  left: "inline-start",
};

function readBorderSide(
  el: Element,
  side: "top" | "right" | "bottom" | "left",
): BorderSide {
  const logical = PHYSICAL_TO_LOGICAL[side];
  const widthRaw =
    readStyle(el, `border-${side}-width`) ||
    readStyle(el, `border-${logical}-width`);
  const widthParsed = widthRaw ? Number.parseFloat(widthRaw) : 0;
  const width =
    Number.isFinite(widthParsed) && widthParsed > 0 ? widthParsed : 0;
  const colorRaw =
    readStyle(el, `border-${side}-color`) ||
    readStyle(el, `border-${logical}-color`);
  const color = colorRaw ? (cssColorToHex(colorRaw) ?? colorRaw) : "#000000";
  return { width, color };
}

const COLOR_TOKEN = /(#[0-9a-f]+|rgba?\([^)]+\)|hsla?\([^)]+\))/i;
const WHITESPACE = /\s+/;

/**
 * Parse a CSS `box-shadow` into its stacked layers, in CSS source order.
 * All-zero-geometry layers paint nothing and are dropped.
 */
function parseShadow(el: Element): Shadow[] {
  const value = readStyle(el, "box-shadow");
  if (!value || value === "none") return [];
  return splitTopLevel(value)
    .map(parseShadowLayer)
    .filter((s): s is Shadow => s !== null);
}

/** Parse one `box-shadow` layer (`<x> <y> <blur> <spread> <color>`). */
function parseShadowLayer(piece: string): Shadow | null {
  const tokens = piece.trim();
  if (!tokens) return null;
  const colorMatch = COLOR_TOKEN.exec(tokens);
  const matched = colorMatch?.[1] ?? "";
  const color = matched ? (cssColorToHex(matched) ?? matched) : "#00000040";
  const remaining = matched ? tokens.replace(matched, "") : tokens;
  const numbers = remaining
    .split(WHITESPACE)
    .map((p) => Number.parseFloat(p))
    .filter((n) => Number.isFinite(n));
  const x = numbers[0] ?? 0;
  const y = numbers[1] ?? 0;
  const blur = numbers[2] ?? 0;
  const spread = numbers[3] ?? 0;
  if (x === 0 && y === 0 && blur === 0 && spread === 0) return null;
  return { x, y, blur, spread, color };
}

/** Parse one `text-shadow` layer (`<x> <y> <blur> <color>`, no spread). */
function parseTextShadowLayer(tokens: string): TextShadow | null {
  const trimmed = tokens.trim();
  if (!trimmed) return null;
  const colorMatch = COLOR_TOKEN.exec(trimmed);
  const matched = colorMatch?.[1] ?? "";
  const color = matched ? (cssColorToHex(matched) ?? matched) : "#00000040";
  const remaining = matched ? trimmed.replace(matched, "") : trimmed;
  const numbers = remaining
    .split(WHITESPACE)
    .map((p) => Number.parseFloat(p))
    .filter((n) => Number.isFinite(n));
  const x = numbers[0] ?? 0;
  const y = numbers[1] ?? 0;
  const blur = numbers[2] ?? 0;
  // An offset-less, blur-less layer paints nothing — drop it so a cleared
  // shadow parses to an empty list rather than an invisible layer.
  if (x === 0 && y === 0 && blur === 0) return null;
  return { x, y, blur, color };
}

/**
 * Parse a CSS `text-shadow` into its stacked layers, in paint order. The
 * property is a comma-separated list; the color of each layer may appear first
 * or last and be hex or rgb/rgba/hsl. Returns an empty list when absent,
 * `none`, or every layer is invisible.
 */
function parseTextShadow(el: Element): TextShadow[] {
  const value = readStyle(el, "text-shadow");
  if (!value || value === "none") return [];
  return splitTopLevel(value)
    .map(parseTextShadowLayer)
    .filter((layer): layer is TextShadow => layer !== null);
}
